import { seeded, seg, tween } from '../../../core/effect/motion';
import {
  add, canvasOf, cardName, faceArt, fade, fill, place, playerColor, sprite,
} from './common';
import {
  FRAME_URL, PANEL, miniBox, paintPanel, panelAnchors, panelReady, panelSpec, whoFromBoard,
} from './battlePanel';

/**
 * **召喚成功（ステータスのパネル）の組み立て**（通常召喚 Issue #578 v1 / 特殊召喚 Issue #573。指示書 `../direction/summoned.md` / `../direction/spsummoning.md`）。
 * `effects/summoned.js` と `effects/spsummoned.js` はこれを呼ぶだけ（同じ絵。違うのは event とラベル）。
 *
 * 召喚が無効にされずに通ったことと、出たモンスターの**数字**（★・ATK / DEF・表示形式）を伝える。
 * バンク（`summoning`）がイラストと名前を見せたので、ここは攻撃宣言と同じ鋼のパネル（`../fx/battlePanel.js`）で
 * テーマの中で「パネル＝ステータス」に揃える。**別の応答で来る**（間に相手の「発動しますか」が挟まる）ので単独で成立させる。
 *
 * ```
 * 0      召喚したプレイヤーの側（P1 = 左 / P2 = 右）の画面の端から鋼のパネル（攻撃宣言の枠。0.8 倍）が加速して滑り込む
 *        （後ろに硬い線）。止まる位置は攻撃宣言の小さいパネルの場所と同じ列（魔法・罠ゾーンの外側、上端 y = 300）。
 *        イラストの窓にカードのイラスト、名前はパネルの上（その人の色の下線）。数字はまだ無い
 * 200    急停止 → **ヒットストップ 80ms**（色収差 1 フレーム・枠の形の閃光 2 フレーム）。止まった側の縁から硬い火花が数本
 * 280    ★ が上から叩き込まれる（小さく揺れる）
 * 400    ATK の数字（大きく金・132px、見出し ATK）
 * 520    下の段に DEF の数字（60px。見出し DEF）と表示形式 ATTACK（赤）/ DEFENSE（青）
 * 700    見得。盤の出たカードの縁が、その人の色の細い線で 1 回だけ光って消える（帯の中。盤には残さない）
 * 1150   パネルが来た側へ横に抜ける（加速。後ろに硬い線）。1380 で画面の外 → 帯の終わりに何も残らない
 * ```
 *
 * **イベントの `data` は空**（MSG_SUMMONED は 0 byte）。どのカードかは port（`editorPorts/duelEffects.js`）が翻訳のときに付けた
 * `payload.summon`（同じフェイズの直前の `summoning`）で、`history` は読まない（Issue #564 の方針）。
 * ★・ATK / DEF・表示形式は盤面の今の値（`whoFromBoard`）。パネルの絵は `paintPanel` を**部品ごとに別の板**（`parts`）にして
 * 順に叩き込む（攻撃宣言の描き方は変えない。ATK と DEF を両方出す並びは `panelSpec` の `mode: 'summon'`）。
 * 層は HUD の下（`space: 'screen'`）だけ。もやもやした光は使わない。
 */
const D = 1400;
const HIT = 200;            // 急停止
const STOP = 80;            // ヒットストップ
const STAR_HIT = 280;       // ★ の着地
const ATK_HIT = 400;        // ATK の着地
const DEF_HIT = 520;        // DEF ＋ 表示形式の着地
const SLAM_IN = 70;         // 叩き込みの落ちてくる時間
const EDGE = 700;           // 盤のカードの縁の光
const EDGE_END = 940;
const EXIT = 1150;          // はけ始め
const EXIT_END = 1380;      // 画面の外へ出切る（帯の終わりより手前）

const SCALE = 0.8;          // 大きいパネルに対する倍率
const SPARKS = 9;
const SPARK_LIFE = 0.32;    // 秒（280 + 320 < 700）

/** ヒットストップの時間の写し（止めの間は HIT で止まる） */
const hs = (t) => (t < HIT ? t : (t < HIT + STOP ? HIT : t - STOP));

/**
 * 叩き込み: 大きい状態（1.9 倍）で上から落ちてきて（power2_in）着地、着地で少し沈んで戻る。
 * `s` = 倍率 / `dy` = 上へのずれ / `on` = 見えているか
 */
function slam(t, hit) {
  const k = tween(t, hit - SLAM_IN, hit, 'power2_in');
  const b = t >= hit ? 1 - 0.05 * Math.sin(seg(t, hit, hit + 120) * Math.PI) : 1;
  return { s: (1.9 - 0.9 * k) * b, dy: -110 * (1 - k), on: t >= hit - SLAM_IN };
}

/**
 * **召喚成功のパネルの spec**（`defineEffect` に渡す）。
 *
 * @param {object} o
 * @param {string} o.event `summoned` / `spsummoned`
 * @param {string} o.label
 * @param {string} o.color 帯の色
 */
export function summonPanel({ situation, label, color }) {
  return {
    situation,
    label,
    color,
    /*
     * どのカードかは port が付けた `summon`（無ければ帯を作らない）。★・ATK / DEF・表示形式は盤面の今の値
     * （`summoning` の時点から変わっていることは無い。まだ効果は解決していない）。盤に居なければ宣言の表示形式のまま
     */
    opens: ({ payload, board }) => {
      const s = payload?.summon;
      if (!s || (!s.uid && !s.code)) return null;
      const who = whoFromBoard({
        uid: s.uid ?? null, code: s.code ?? null, faceUp: s.position?.faceUp ?? true, defense: Boolean(s.position?.defense),
      }, board);
      return {
        ...payload,
        uid: s.uid ?? null,
        code: who.code ?? s.code ?? null,   // 絵の到着で器が組み直す
        zone: s.zone ?? null,
        player: s.player === 1 ? 1 : 0,
        who,
      };
    },
    duration: () => D,
    scene: [
      'reset',
      {
        ms: 0,
        grade: {
          // 色収差は急停止の 1 フレームだけ。止めの間はコントラストを上げて硬く
          chroma: { keys: [{ ms: HIT - 1, value: 0 }, { ms: HIT, value: 0.012 }, { ms: HIT + 34, value: 0, ease: 'ease_out' }] },
          contrast: { keys: [{ ms: HIT - 1, value: 1 }, { ms: HIT, value: 1.2 }, { ms: HIT + STOP, value: 1.2, ease: 'hold' }, { ms: HIT + STOP + 1, value: 1 }] },
        },
      },
      { ms: HIT + STOP, shake: { amp: 12, freq: 26, decayMs: 200 } },
      { ms: STAR_HIT, shake: { amp: 6, freq: 30, decayMs: 140 } },
      { ms: ATK_HIT, shake: { amp: 6, freq: 30, decayMs: 140 } },
      { ms: DEF_HIT, shake: { amp: 6, freq: 30, decayMs: 140 } },
    ],
    sounds: () => [
      { at: HIT - 10, name: 'attack', gain: 0.45 },
      { at: STAR_HIT - 10, name: 'set_card', gain: 0.55 },
      { at: ATK_HIT - 10, name: 'set_card', gain: 0.6 },
      { at: DEF_HIT - 10, name: 'set_card', gain: 0.55 },
      { at: EDGE, name: 'attack_up', gain: 0.45 },
    ],

    layers: [
      {
        id: 'panel',
        label: 'ステータスのパネル（滑り込み → 数字の叩き込み → 盤のカードの縁の光 → 横に抜ける）',
        space: 'screen',
        script: (ctx) => {
          const { THREE, kit } = ctx;
          const { w: W } = canvasOf(ctx);
          const { payload } = ctx;
          const root = new THREE.Group();
          if (!payload?.who) return { root };
          const player = payload.player === 1 ? 1 : 0;
          const zq = (k) => ctx.stage?.field?.zoneQuad?.(k) ?? null;

          // --- 止まる場所: 攻撃宣言の小さいパネルと同じ列（そのプレイヤーの魔法・罠ゾーンの外側の端、上端 y = 300）
          const mini = miniBox(zq, player, canvasOf(ctx));
          const left = mini.x + mini.w / 2 < W / 2;
          const side = left ? -1 : 1;                 // 来る側（-1 = 左 / +1 = 右）
          const PW = PANEL.w * SCALE; const PH = PANEL.h * SCALE;
          const cx = left ? mini.x + PW / 2 : mini.x + mini.w - PW / 2;
          const cy = mini.y + PH / 2;
          const frameCY = cy + (PANEL.nameH / 2) * SCALE;
          const offDist = (left ? cx : W - cx) + PW / 2 + 40;   // 画面の外まで

          const s0 = panelSpec(payload.who, { attacker: false, player, side, mode: 'summon' });
          const spec = { ...s0, color: playerColor(ctx, player), name: s0.faceDown ? s0.name : (cardName(ctx, s0.code) || s0.name) };
          const art = faceArt(ctx, { code: spec.code, player, faceDown: spec.faceDown });
          const imgs = { art };
          const A = panelAnchors(spec);

          // --- パネル（group を 0.8 倍。中は canvas のピクセル）
          const g = new THREE.Group();
          add(root, g, 10);
          let order = 10;
          /** 部品の板: canvas にその部品だけ描いて貼る。`pivot` は拡大の中心（canvas の座標） */
          const part = (paints, pivot) => {
            const c = typeof document !== 'undefined' ? document.createElement('canvas') : null;
            if (c) { c.width = PANEL.w; c.height = Math.ceil(PANEL.h); }
            const tex = c ? kit.textureFromCanvas(c) : null;
            const paint = () => {
              const g2 = c?.getContext?.('2d');
              if (!g2) return;
              g2.setTransform(1, 0, 0, 1, 0, 0);
              g2.clearRect(0, 0, c.width, c.height);
              paints.forEach(([layer, names]) => paintPanel(g2, spec, layer, imgs, names));
              if (tex) tex.needsUpdate = true;
            };
            paint();
            const mesh = kit.quad(kit.plain(tex, 1), PANEL.w, Math.ceil(PANEL.h));
            order += 0.01;
            mesh.renderOrder = order;
            g.add(mesh);
            return { mesh, paint, pivot: pivot ? [pivot[0] - PANEL.w / 2, pivot[1] - PANEL.h / 2] : [0, 0] };
          };
          const body = part([['base', ['ground', 'art', 'underline', 'frame']], ['small', ['name']]], null);
          const stars = part([['small', ['stars']]], A.stars);
          const atk = part([['base', ['number']], ['small', ['label']]], A.number);
          const def = part([['small', ['sub']]], A.sub);
          const position = part([['small', ['position']]], A.position);
          const parts = [body, stars, atk, def, position];
          let ready = JSON.stringify(panelReady(imgs));
          // 急停止の閃光（枠の形。2 フレーム）
          const flash = sprite(ctx, kit.urlTexture(FRAME_URL), PANEL.w, PANEL.frameH, { blend: 'add' });
          flash.position.set(0, PANEL.nameH / 2, 0);
          flash.scale.set(side === 1 ? -1 : 1, 1, 1);
          flash.renderOrder = order + 0.05;
          g.add(flash);

          // --- 硬い線（滑り込み・はけの後ろ。3 本）
          const trails = [-0.3, 0.05, 0.34].map((k, i) => ({ m: add(root, fill(ctx, i === 1 ? '#ffd21a' : '#ffffff', 1, 1), 9), k }));

          // --- 急停止の火花（止まった側＝進行方向の縁から、進行方向へ扇に）
          const rand = seeded(`${ctx.seed}:summoned`);
          const sparkCols = ['#ffffff', '#ffd21a', '#fff3b0', '#ffb020'];
          const sparks = Array.from({ length: SPARKS }, (_, i) => {
            const m = add(root, fill(ctx, sparkCols[i % sparkCols.length], 1, 1), 12);
            const base = side === -1 ? 0 : Math.PI;   // 進行方向（左から来たパネルは右へ）
            return {
              m, a: base + (rand() - 0.5) * 1.1, ey: (rand() - 0.5) * PANEL.frameH * SCALE * 0.9,
              len: 70 + rand() * 110, w: 3 + rand() * 3, speed: 700 + rand() * 900,
            };
          });
          const edgeX = cx - side * PW / 2;

          // --- 盤の出たカードの縁（その人の色の細い線。4 辺）
          const quad = payload.zone ? zq(payload.zone) : null;
          const qc = quad ? quad.reduce((a, p) => [a[0] + p[0] / 4, a[1] + p[1] / 4], [0, 0]) : null;
          const edges = quad ? [0, 1, 2, 3].map(() => add(root, fill(ctx, spec.color, 1, 1), 8)) : [];

          return {
            root,
            update: ({ localMs: t }) => {
              const r = JSON.stringify(panelReady(imgs));
              if (r !== ready) { ready = r; parts.forEach((p) => p.paint()); }
              const u = hs(t);
              const on = t >= 0 && t < EXIT_END;

              // --- パネル: 加速して滑り込み → 急停止（反動）→ 見得 → 来た側へ加速して抜ける
              const inK = tween(t, 0, HIT, 'power2_in');
              const recoil = t >= HIT ? Math.sin(seg(u, HIT, HIT + 90) * Math.PI) * (1 - seg(u, HIT, HIT + 90)) : 0;
              const outK = tween(t, EXIT, EXIT_END, 'power3_in');
              const startX = cx + side * offDist;
              const x = startX + (cx - startX) * inK + side * 12 * recoil + side * offDist * outK;
              place(g, x, cy, { s: SCALE });
              fade(g, on ? 1 : 0);
              fade(flash, t >= HIT && t < HIT + 34 ? 0.5 : 0);

              // --- 部品: 本体はそのまま、★ → ATK → DEF ＋ 表示形式 の順に上から叩き込む（拡大の中心はその部品の場所）
              const put = (p, hit) => {
                const { s, dy, on: shown } = slam(t, hit);
                p.mesh.position.set(p.pivot[0] * (1 - s), p.pivot[1] * (1 - s) + dy, 0);
                p.mesh.scale.set(s, s, 1);
                fade(p.mesh, on && shown ? 1 : 0);
              };
              body.mesh.position.set(0, 0, 0);
              fade(body.mesh, on ? 1 : 0);
              put(stars, STAR_HIT);
              put(atk, ATK_HIT);
              put(def, DEF_HIT);
              put(position, DEF_HIT);

              // --- 硬い線: 滑り込みは来た側の縁から後ろへ、はけは反対の縁から後ろへ
              let len = 0; let tail = 0; let dir = side;
              if (t < EXIT) {
                len = t < HIT ? W * 0.4 * seg(t, 0, 40) : W * 0.4 * (1 - seg(u, HIT, HIT + 80));
                tail = x + side * PW / 2;
              } else {
                dir = -side;
                len = W * 0.5 * seg(t, EXIT + 30, EXIT + 90) * outK;
                tail = x - side * PW / 2;
              }
              trails.forEach(({ m, k }) => {
                place(m, tail + dir * len / 2, frameCY + k * PANEL.frameH * SCALE, { sx: Math.max(0.001, len), sy: k === 0.05 ? 6 : 3 });
                fade(m, on && len > 1 ? 0.9 : 0);
              });

              // --- 火花（急停止の瞬間に出て、止めの間は止まる）
              const since = Math.max(0, (u - HIT) / 1000);
              sparks.forEach((s) => {
                const k = Math.min(1, since / SPARK_LIFE);
                const rr = s.speed * since * (1 - 0.4 * k);
                const l = s.len * (1 - k);
                const mid = rr + l / 2;
                place(s.m, edgeX + Math.cos(s.a) * mid, frameCY + s.ey + Math.sin(s.a) * mid, { sx: Math.max(0.001, l), sy: s.w, rot: s.a });
                fade(s.m, t >= HIT && k < 1 ? 1 : 0);
              });

              // --- 盤のカードの縁: 1 回だけ光って（少し外へ広がりながら）消える
              if (quad) {
                const grow = 1 + 0.12 * tween(t, EDGE, EDGE_END, 'ease_out');
                const ea = seg(t, EDGE, EDGE + 20) * (1 - seg(t, EDGE + 80, EDGE_END));
                const pts = quad.map((p) => [qc[0] + (p[0] - qc[0]) * grow, qc[1] + (p[1] - qc[1]) * grow]);
                edges.forEach((m, i) => {
                  const a = pts[i]; const b = pts[(i + 1) % 4];
                  const dx = b[0] - a[0]; const dy = b[1] - a[1];
                  place(m, (a[0] + b[0]) / 2, (a[1] + b[1]) / 2, { sx: Math.max(0.001, Math.hypot(dx, dy) + 4), sy: 5 - 2 * seg(t, EDGE, EDGE_END), rot: Math.atan2(dy, dx) });
                  fade(m, ea);
                });
              }
            },
          };
        },
      },
    ],
  };
}
